import React, { ReactNode } from 'react';
import { closeSnackbar, enqueueSnackbar, SnackbarAction } from 'notistack';
import * as StackTrace from 'stacktrace-js';
import { Box, Typography } from '@mui/material';

interface ErrorBoundaryProps {
    children: ReactNode | ReactNode[];
}

interface ErrorBoundaryState {
    hasError: boolean;
    error: Error;
    stackframes: StackTrace.StackFrame[];
    errorInfo: React.ErrorInfo;
}

/**
 * React error boundary based on documentation: https://reactjs.org/docs/error-boundaries.html
 * Displays errors as snackbar messages to the user.
 */
export class ErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
    public getErrorMessage(error: Error): string {
        if (!error.name) {
            error.name = 'Promise.Rejection';
        }
        return `[${error.name}] ${error.message}`;
    }

    public getCallStack(stackframes: StackTrace.StackFrame[]): string {
        return stackframes
            .map((frame) => {
                return `   at ${frame?.toString()}`;
            })
            .join('\n');
    }

    public getErrorStringWithCallstack(error: Error, stackframes: StackTrace.StackFrame[]): string {
        return `Error: ${this.getErrorMessage(error)}\n${this.getCallStack(stackframes)}`;
    }

    public addToNotiStack(message: string, stackframes: StackTrace.StackFrame[]): void {
        // You can view the information in an alert to see things working like this:
        enqueueSnackbar(
            `Timestamp: ${new Intl.DateTimeFormat('en', {
                day: '2-digit',
                month: '2-digit',
                year: 'numeric',
                hour: '2-digit',
                minute: '2-digit',
                second: '2-digit',
            }).format(new Date())}\nError; ${message}\n at ${stackframes[0].toString()}\n${
                stackframes[1] ? stackframes[1].toString() : ''
            }`,
            {
                variant: 'error',
                style: { whiteSpace: 'pre-line' },
                action: closeSnackbar as SnackbarAction,
            }
        );
    }

    public static getDerivedStateFromError() {
        return { hasError: true };
    }

    public componentDidMount() {
        window.addEventListener('unhandledrejection', this.promiseRejectionHandler);
    }

    public componentWillUnmount() {
        window.removeEventListener('unhandledrejection', this.promiseRejectionHandler);
    }

    /**
     * Makes promise rejections available to the error boundaries error handling and display
     * mechanism.
     */
    private promiseRejectionHandler = (event: PromiseRejectionEvent) => {
        if (event && event.reason) {
            this.handleError(event.reason);
        }
    };

    public errback(err: Error): void {
        if (err.message) {
            console.error('error:', err.message);
        } else {
            console.error('Error occurred');
        }
    }

    private handleError(error: Error, errorInfo?: React.ErrorInfo) {
        if (errorInfo) {
            console.error('errorInfo:', errorInfo?.componentStack);
        }

        if (error.message) {
            console.error('error:', error.message);
        } else {
            console.error('Error occurred');
        }

        this.processError(error);
    }

    public processError(error: Error | undefined) {
        if (!error) {
            return;
        }
        // callback is called with an Array[StackFrame]
        StackTrace.fromError(error)
            .then((stackframes) => {
                this.setState({ ...this.state, stackframes });
                this.addToNotiStack(this.getErrorMessage(error), stackframes);
            })
            .catch(this.errback);
    }

    public componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
        this.handleError(error, errorInfo);
    }
    public constructor(props: ErrorBoundaryProps) {
        super(props);
        this.state = { hasError: false } as ErrorBoundaryState;
        this.addToNotiStack = this.addToNotiStack.bind(this);
        this.getErrorStringWithCallstack = this.getErrorStringWithCallstack.bind(this);
        this.getErrorMessage = this.getErrorMessage.bind(this);
        this.getCallStack = this.getCallStack.bind(this);

        window.onerror = (_msg, _url, _line, _col, error) => {
            this.processError(error);

            // If you return true, then error alerts (like in older versions of
            // Internet Explorer) will be suppressed.
            return true; // suppressErrorAlert;
        };
    }

    public render() {
        if (this.state.hasError) {
            // You can render any custom fallback UI
            return (
                <Box margin="5%">
                    <Typography variant="h5" color="error">
                        {this.state.error && this.getErrorMessage(this.state.error)}
                    </Typography>
                    <Box margin="10px">
                        {this.state.stackframes &&
                            this.state.stackframes.map((frame) => {
                                return (
                                    <Typography
                                        variant="body1"
                                        key={`stackframe${frame.lineNumber}`}
                                    >{`   at ${frame?.toString()}`}</Typography>
                                );
                            })}
                    </Box>
                    <Typography variant="body2">
                        {this.state.errorInfo && this.state.errorInfo.componentStack}
                    </Typography>
                </Box>
            );
        }

        return this.props.children;
    }
}

export default ErrorBoundary;
